// stop-on-reply-edit.ts — editing 'Stop on reply' on an ALREADY-SCHEDULED /
// active sequence (FE#213).
//
// Pre-launch the toggle lives in the wizard (MessageStep / FinishStep). Once a
// drip is launched there was no way to change it from the detail view — the
// header showed only a read-only "stops on reply" pill — so an owner with many
// copied campaigns (copies inherit the flag) had to ask an operator to run a
// mongo update-many {stopOnReply:false}. These helpers back a live toggle on the
// detail header that patches the drip through the normal dbService path
// (DBDripThunks.patch → optimistic store update + persisted), exactly like the
// header's rename and per-step gap edits.

import type { Drip } from '@bt-common/schemas/bt/drip';

/** Whether the 'Stop on reply' toggle may be edited on the detail view.
 *  Only a REAL drip document carries a persisted `stopOnReply`; a legacy
 *  standalone campaign is rendered through a synthesized 1-step bridge with a
 *  hard-coded `stopOnReply: false` and no drip doc to patch, so it stays
 *  read-only. Editable across every non-terminal launched state (active /
 *  paused) — the flag governs future follow-ups, which are exactly what a
 *  launched, still-running sequence still has ahead of it. */
export function canEditStopOnReply(realDrip: Drip | undefined, isStandalone: boolean): boolean {
  return Boolean(realDrip) && !isStandalone;
}

/** The current effective value. An absent field reads as false, mirroring the
 *  backend, which exits a recipient only on `stopOnReply === true`. */
export function currentStopOnReply(drip: Pick<Drip, 'stopOnReply'> | undefined): boolean {
  return drip?.stopOnReply === true;
}

/** The entity to hand DBDripThunks.patch — the whole drip with only
 *  `stopOnReply` flipped. Spreads the full document because the dbService patch
 *  path replaces the entity (the same shape used by the detail's rename and
 *  gap-edit handlers). Non-mutating: the source drip is left untouched. */
export function stopOnReplyPatch(realDrip: Drip, next: boolean): Drip {
  return { ...realDrip, stopOnReply: next };
}
